import React, { useState } from 'react';
import DiscardConfirmModal from '../DiscardConfirmModal';
import '../Modal.css';

/**
 * Reusable modal wrapper.
 *
 * Props:
 *   isOpen    – boolean (renders nothing when false)
 *   onClose   – called when overlay or ✕ is clicked
 *   title     – modal heading text
 *   wide      – if true, applies .modal-wide (560 px)
 *   className – extra class(es) on the .modal element
 *   actions   – ReactNode rendered inside .modal-actions
 *   actionsSpread – if true, uses .modal-actions-spread instead
 *   isDirty   – if true, shows discard confirmation before closing
 *   closeOnBackdrop – if false, clicking the overlay does not close the modal (default true)
 *   children  – modal body content
 */
export default function ModalTemplate({
  isOpen = true,
  onClose,
  title,
  wide = false,
  className = '',
  actions,
  actionsSpread = false,
  isDirty = false,
  closeOnBackdrop = true,
  children,
}) {
  const [showDiscard, setShowDiscard] = useState(false);

  if (!isOpen) return null;

  const handleClose = () => {
    if (isDirty) {
      setShowDiscard(true);
    } else {
      onClose();
    }
  };

  const handleBackdropClick = () => {
    if (!closeOnBackdrop) return;
    handleClose();
  };

  const modalClasses = ['modal', wide && 'modal-wide', className]
    .filter(Boolean)
    .join(' ');

  const actionsClass = actionsSpread ? 'modal-actions-spread' : 'modal-actions';

  return (
    <>
      <div className="modal-overlay" onClick={handleBackdropClick}>
        <div className={modalClasses} onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2>{title}</h2>
            <button className="modal-close" onClick={handleClose}>✕</button>
          </div>

          {children}

          {actions && <div className={actionsClass}>{actions}</div>}
        </div>
      </div>
      {showDiscard && (
        <DiscardConfirmModal
          onDiscard={() => { setShowDiscard(false); onClose(); }}
          onKeepEditing={() => setShowDiscard(false)}
        />
      )}
    </>
  );
}
